import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { RocketLaunch, CheckCircle, CircleNotch, XCircle, Circle } from "@phosphor-icons/react";
import { api, act, useFetch, fmtDate } from "@/lib/api";
import { PageHeader, Panel, Badge, Stat } from "@/components/common";
import { LaunchResults } from "@/components/LaunchResults";
import { Rich } from "@/components/Rich";
import { EmailPreview } from "@/components/EmailPreview";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const PIECES = [["contacts", "Contacts"], ["product", "Offer facts"], ["campaign", "Emails"], ["newsletter", "Newsletter"], ["social", "Social posts"], ["carousel", "Carousel"], ["video", "Video"]];
const WHY = { deliverability_risky: "Shared inbox address (info@, admin@…)", deliverability_unverified: "Email not verified", permission_unknown: "Permission not confirmed",
  permission_withdrawn: "Opted out", suppressed: "On the opt-out list", tenant_paused: "Kill switch is on" };
const ICON = { done: [CheckCircle, "text-[#00C781]"], working: [CircleNotch, "text-[#C084FC] animate-spin"], failed: [XCircle, "text-[#FF3B30]"], waiting: [Circle, "text-zinc-600"] };

const Progress = ({ p }) => (
  <div className="grid grid-cols-2 md:grid-cols-7 gap-2" data-testid="launch-progress">
    {PIECES.map(([k, label]) => {
      const s = p?.[k] || { status: "waiting" };
      const [I, cls] = ICON[s.status] || ICON.waiting;
      return (
        <div key={k} className="border border-border rounded-md p-3 bg-[#121212]" data-testid={`launch-step-${k}`}>
          <div className="flex items-center gap-2 text-sm font-medium"><I size={16} weight="bold" className={cls} />{label}</div>
          <div className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{s.detail || s.status}</div>
        </div>
      );
    })}
  </div>
);

const Audience = ({ a }) => (
  <Panel title="Who will receive it" data-testid="launch-audience">
    <div className="grid grid-cols-2 gap-3 mb-4">
      <Stat label="Contacts in your list" value={a.total} testid="launch-audience-total" />
      <Stat label="Can be emailed now" value={a.eligible} tone="green" testid="launch-audience-eligible" />
    </div>
    {Object.entries(a.blocked_by).map(([k, n]) => (
      <div key={k} className="flex justify-between text-xs py-1 border-b border-border/50"><span className="text-zinc-400">{WHY[k] || k.replace(/_/g, " ")}</span><span className="font-mono">{n}</span></div>
    ))}
  </Panel>
);

const Media = ({ a, className }) => !a ? null : a.status !== "ready"
  ? <div className={`grid place-items-center bg-black border border-border rounded-md text-xs text-zinc-500 ${className}`}>{a.status === "failed" ? "Failed" : "Rendering…"}</div>
  : a.kind === "video" ? <video src={a.url} controls className={`rounded-md border border-border ${className}`} /> : <img src={a.url} alt="" className={`rounded-md border border-border object-cover ${className}`} />;

const Emails = ({ c }) => (
  <Panel title="Email sequence" action={<Link to={`/campaigns/${c.id}`} className="text-xs text-[#007AFF]" data-testid="launch-edit-emails">Edit emails</Link>}>
    <div className="space-y-4" data-testid="launch-emails">{c.steps.map((s, i) => (
      <div key={i} className="border-l-2 border-[#A855F7] pl-4">
        <div className="text-[11px] font-mono text-zinc-500">DAY {s.day}</div>
        <div className="font-semibold text-sm">{s.subject}</div>
        <p className="text-sm text-zinc-300 whitespace-pre-wrap mt-1"><Rich text={s.body} /></p>
      </div>
    ))}</div>
  </Panel>
);

const Newsletter = ({ n, img }) => (
  <Panel title="Launch newsletter" action={<Link to="/studio" className="text-xs text-[#007AFF]">Edit in Studio</Link>}>
    <div data-testid="launch-newsletter">
      <Media a={img} className="w-full h-40 mb-3" />
      <div className="text-[11px] font-mono text-zinc-500">SUBJECT</div><div className="font-semibold text-sm mb-2">{n.content.subject}</div>
      <h4 className="text-lg font-bold">{n.content.headline}</h4><p className="text-sm text-zinc-300 mt-1"><Rich text={n.content.intro} /></p>
      {n.content.sections.map((s, i) => <div key={i} className="mt-3"><div className="font-semibold text-sm">{s.heading}</div><p className="text-sm text-zinc-400 whitespace-pre-wrap"><Rich text={s.body} /></p></div>)}
    </div>
  </Panel>
);

const Social = ({ p, img }) => (
  <Panel title="Social posts">
    <div className="grid md:grid-cols-3 gap-3" data-testid="launch-social">{Object.entries(p.captions).map(([net, t]) => (
      <div key={net} className="border border-border rounded-md p-3"><Badge v={net} tone="blue" /><Media a={img} className="w-full h-28 my-2" /><p className="text-xs text-zinc-300 whitespace-pre-wrap"><Rich text={t} /></p>{!t.includes("Post from MaSa - Specia AI Agent") && <p className="text-xs text-white font-bold mt-2" data-testid={`social-disclaimer-${net}`}>Post from MaSa - Specia AI Agent</p>}</div>
    ))}</div>
  </Panel>
);

const Visuals = ({ car, video }) => (
  <Panel title="Carousel & video">
    <div className="grid grid-cols-5 gap-2" data-testid="launch-carousel">{(car?.slide_assets || []).map((a, i) => <Media key={i} a={a} className="w-full aspect-[4/5]" />)}</div>
    <div className="mt-4" data-testid="launch-video">{video ? <Media a={video} className="w-full max-h-80" /> : <div className="text-xs text-zinc-500">Video not started</div>}</div>
  </Panel>
);

const LaunchButton = ({ L, onDone }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    const r = await act(() => api.post(`/launches/${L.id}/launch`), "Campaign launched");
    setBusy(false);
    if (r) onDone();
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button data-testid="launch-go-btn" disabled={L.status !== "ready" || busy} className="gap-2 bg-gradient-to-r from-[#6D28D9] via-[#C026D3] to-[#F97316] text-white hover:opacity-90"><RocketLaunch weight="bold" /> Approve & launch</Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#121212] border-border">
        <AlertDialogHeader>
          <AlertDialogTitle>Send to {L.audience.eligible} contacts?</AlertDialogTitle>
          <AlertDialogDescription>This approves the emails, newsletter, posts and visuals. Emails go out in daily batches within your send limit, starting now; the newsletter is scheduled for the next weekday at 10am Sydney. In test mode every email goes to your test inbox instead.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="launch-go-cancel">Not yet</AlertDialogCancel>
          <AlertDialogAction data-testid="launch-go-confirm" onClick={go}>Yes, launch</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const Feedback = ({ L, onDone }) => {
  const [t, setT] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    const r = await act(() => api.post(`/launches/${L.id}/feedback`, { text: t }), "Updated with your feedback");
    setBusy(false);
    if (r) { setT(""); onDone(); }
  };
  return (
    <Panel title="Tell MaSa what to change">
      <Textarea data-testid="launch-feedback-input" rows={3} value={t} onChange={(e) => setT(e.target.value)} className="bg-black"
        placeholder="e.g. Make the emails shorter and friendlier, lead with Gary saving time on workpapers, mention the free setup earlier" />
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px] text-zinc-500">Rewrites the emails, newsletter and posts together. Facts stay from your offer text.</span>
        <Button data-testid="launch-feedback-btn" size="sm" disabled={t.trim().length < 3 || busy} onClick={send}>{busy ? "Rewriting…" : "Improve it"}</Button>
      </div>
      {(L.feedback || []).slice().reverse().map((f, i) => (
        <div key={i} className="mt-3 text-xs border-l-2 border-[#F97316] pl-3" data-testid={`launch-feedback-${i}`}>
          <div className="text-zinc-300">“{f.text}”</div><div className="text-zinc-500 mt-0.5">{f.summary} · {fmtDate(f.at)}</div>
        </div>
      ))}
    </Panel>
  );
};

export default function LaunchReview() {
  const { id } = useParams();
  const { data: L, reload } = useFetch(`/launches/${id}`);
  const rendering = L && (L.status === "building" || L.video?.status === "generating" || (L.carousel?.slide_assets || []).some((a) => a?.status === "generating"));
  useEffect(() => { if (!rendering) return undefined; const t = setInterval(reload, 6000); return () => clearInterval(t); }, [rendering, reload]);
  if (!L) return null;
  const res = L.launch_result;
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={`Launchpad · ${fmtDate(L.created_at)}`} title={L.name} desc={L.status === "launched" ? `Launched ${fmtDate(L.launched_at)} by ${L.launched_by}` : L.status === "building" ? "MaSa is building your kit — this takes a few minutes." : "Review everything below, then approve to launch."}>
        <Badge v={L.status} tone={L.status === "launched" ? "green" : L.status === "failed" ? "red" : "blue"} data-testid="launch-status" />
        {L.status === "failed" && <Button variant="secondary" data-testid="launch-retry-btn" onClick={async () => { if (await act(() => api.post(`/launches/${L.id}/retry`), "Continuing the build")) reload(); }}>Retry</Button>}
        {L.status !== "launched" && <LaunchButton L={L} onDone={reload} />}
      </PageHeader>
      {L.error && <div className="text-sm text-[#FF3B30] border border-[#FF3B30]/40 rounded-md p-3" data-testid="launch-error">{L.error}</div>}
      {res && <div className="text-sm border border-[#00C781]/40 rounded-md p-3" data-testid="launch-result">{res.enrolled} contacts enrolled · approved: {res.approved.join(", ") || "—"}{res.newsletter_at && ` · newsletter ${fmtDate(res.newsletter_at)}`}{res.skipped.length > 0 && ` · needs attention: ${res.skipped.join("; ")}`}</div>}
      <Progress p={L.progress} />
      {L.status === "launched" && <LaunchResults id={L.id} />}
      <div className="grid xl:grid-cols-[1fr_380px] gap-6">
        <div className="space-y-6">
          {["ready", "launched"].includes(L.status) && L.campaign && <Feedback L={L} onDone={reload} />}
          {L.campaign && <Emails c={L.campaign} />}
          {L.campaign && <EmailPreview launchId={L.id} campaignId={L.campaign.id} version={L.campaign.updated_at} />}
          {L.newsletter && <Newsletter n={L.newsletter} img={L.image} />}
          {L.social && <Social p={L.social} img={L.image} />}
          {(L.carousel || L.video) && <Visuals car={L.carousel} video={L.video} />}
        </div>
        <div className="space-y-6">
          <Panel title="Offering image"><Media a={L.image} className="w-full" /></Panel>
          <Audience a={L.audience} />
        </div>
      </div>
    </div>
  );
}
